import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Trash2, GripVertical, Save, ChevronLeft, AlertCircle, ChevronDown } from "lucide-react";
import api from "@/api";
import { PostProcessStepRow, AddStepDropdown, getDefaultParams } from "@/components/PostProcessEditor";

const FIELD_TYPES = ["text", "number", "date", "email", "phone", "boolean", "select", "textarea"];

const newField = () => ({
  id: crypto.randomUUID(),
  name: "",
  label: "",
  field_type: "text",
  regex: "",
  description: "",
  required: false,
  enabled: true,
  example: "",
  regex_group: 0,
  regex_multiple: false,
  is_definition_regex: false,
  post_processing: [],
});

const EMPTY_FORM = {
  name: "",
  description: "",
  icon: "FileText",
  fields: [newField()],
  definition_match_mode: "any",
  identifier_start_patterns: [],
  identifier_start_mode: "and",
  identifier_end_patterns: [],
  identifier_end_mode: "and",
  call_llm: true,
};

export default function SchemaBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [expandedFields, setExpandedFields] = useState({});

  useEffect(() => {
    if (!id) return;
    api.get(`/schemas/${id}`).then(r => {
      const d = r.data;
      setForm({
        name: d.name,
        description: d.description || "",
        icon: d.icon || "FileText",
        fields: d.fields?.length > 0 ? d.fields : [newField()],
        definition_match_mode: d.definition_match_mode || "any",
        identifier_start_patterns: d.identifier_start_patterns || [],
        identifier_start_mode: d.identifier_start_mode || "and",
        identifier_end_patterns: d.identifier_end_patterns || [],
        identifier_end_mode: d.identifier_end_mode || "and",
        call_llm: d.call_llm ?? true,
      });
    }).catch(() => setError("Schema not found")).finally(() => setLoading(false));
  }, [id]);

  const updateField = useCallback((idx, key, val) => {
    setForm(f => {
      const fields = [...f.fields];
      fields[idx] = { ...fields[idx], [key]: val };
      return { ...f, fields };
    });
  }, []);

  const addField = () => {
    setForm(f => ({ ...f, fields: [...f.fields, newField()] }));
  };

  const removeField = (idx) => {
    setForm(f => ({ ...f, fields: f.fields.filter((_, i) => i !== idx) }));
    setExpandedFields(prev => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  };

  const toggleExpand = (idx) => setExpandedFields(prev => ({ ...prev, [idx]: !prev[idx] }));

  // Field post-processing helpers
  const addFieldStep = (fieldIdx, type) => {
    const steps = [...(form.fields[fieldIdx].post_processing || []), { type, params: getDefaultParams(type) }];
    updateField(fieldIdx, "post_processing", steps);
  };
  const removeFieldStep = (fieldIdx, stepIdx) => {
    updateField(fieldIdx, "post_processing", (form.fields[fieldIdx].post_processing || []).filter((_, i) => i !== stepIdx));
  };
  const updateFieldStepParams = (fieldIdx, stepIdx, key, val) => {
    const steps = [...(form.fields[fieldIdx].post_processing || [])];
    steps[stepIdx] = { ...steps[stepIdx], params: { ...steps[stepIdx].params, [key]: val } };
    updateField(fieldIdx, "post_processing", steps);
  };

  // Scope pattern helpers
  const addScopePattern = (type) => {
    const key = type === "start" ? "identifier_start_patterns" : "identifier_end_patterns";
    setForm(f => ({ ...f, [key]: [...(f[key] || []), { regex: "" }] }));
  };
  const removeScopePattern = (type, idx) => {
    const key = type === "start" ? "identifier_start_patterns" : "identifier_end_patterns";
    setForm(f => ({ ...f, [key]: (f[key] || []).filter((_, i) => i !== idx) }));
  };
  const updateScopePattern = (type, idx, regex) => {
    const key = type === "start" ? "identifier_start_patterns" : "identifier_end_patterns";
    setForm(f => {
      const patterns = [...(f[key] || [])];
      patterns[idx] = { regex };
      return { ...f, [key]: patterns };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Schema name is required"); return; }
    const validFields = form.fields.filter(f => f.name && f.label);
    if (validFields.length === 0) { setError("At least one field with a label and name is required"); return; }
    setSaving(true);
    setError("");
    try {
      const payload = { ...form, fields: validFields };
      if (id) {
        await api.put(`/schemas/${id}`, payload);
      } else {
        await api.post("/schemas", payload);
      }
      navigate("/schemas");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save schema");
    } finally {
      setSaving(false);
    }
  };

  const hasAdvancedConfig = (field) =>
    (field.regex_group > 0) || field.regex_multiple || (field.post_processing?.length > 0);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-[#006080] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="schema-builder-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/schemas")} className="p-1.5 hover:bg-slate-100 rounded text-slate-500">
          <ChevronLeft size={18} />
        </button>
        <h2 className="font-bold text-slate-900 text-lg">{id ? "Edit Schema" : "New Schema"}</h2>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
          <AlertCircle size={14} className="flex-shrink-0" /> {error}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        {/* ── Schema Details ── */}
        <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Schema Details</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Name <span className="text-red-400">*</span></label>
              <input
                data-testid="schema-name-input"
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                required
                placeholder="e.g., Invoice, Contract, ID Card"
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Description</label>
              <input
                data-testid="schema-description-input"
                value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Brief description of document type"
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
              />
            </div>
          </div>

          {/* Auto-ID Match Mode */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Auto-ID Match Mode</label>
            <div className="flex items-center gap-2 flex-wrap">
              {[{v:"any",l:"Match Any"},{v:"all",l:"Match All"}].map(({v,l}) => (
                <button key={v} type="button" data-testid={`match-mode-${v}`}
                  onClick={() => setForm(f => ({ ...f, definition_match_mode: v }))}
                  className={`h-8 px-3 text-xs font-medium rounded border transition-colors ${
                    form.definition_match_mode === v ? "bg-[#006080] text-white border-[#006080]" : "bg-white text-slate-600 border-slate-200 hover:border-[#006080]/50"
                  }`}>{l}</button>
              ))}
              <span className="text-xs text-slate-400">
                {form.definition_match_mode === "all" ? "All identifier regexes must match" : "At least one identifier regex must match"}
              </span>
            </div>
          </div>

          {/* Call LLM toggle */}
          <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <div>
              <p className="text-sm font-medium text-slate-800">Call LLM for extraction</p>
              <p className="text-xs text-slate-500 mt-0.5">
                When <span className="font-semibold">off</span>, extraction uses regex patterns only — faster and cheaper, but requires well-defined regex on all fields.
                Can be overridden at the Pipeline level.
              </p>
            </div>
            <button
              type="button"
              data-testid="schema-call-llm-toggle"
              onClick={() => setForm(f => ({ ...f, call_llm: !f.call_llm }))}
              className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ml-4 ${form.call_llm ? "bg-[#006080]" : "bg-slate-200"}`}
            >
              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.call_llm ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>

          {/* Identifier Scope */}
          <div className="border border-slate-100 rounded-md p-4 space-y-4">
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-0.5">Identifier Scope</h4>
              <p className="text-xs text-slate-400">Limit schema detection to text between start and end patterns.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Start boundary */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold text-slate-600">Start Boundary</span>
                    <div className="flex gap-1">
                      {["and","or"].map(m => (
                        <button key={m} type="button"
                          onClick={() => setForm(f => ({...f, identifier_start_mode: m}))}
                          className={`h-5 px-1.5 text-[10px] font-bold rounded border transition-colors ${
                            form.identifier_start_mode === m ? "bg-[#006080] text-white border-[#006080]" : "bg-slate-50 text-slate-500 border-slate-200"
                          }`}>{m.toUpperCase()}</button>
                      ))}
                    </div>
                  </div>
                  <button type="button" onClick={() => addScopePattern("start")}
                    className="text-[11px] text-[#006080] hover:underline font-medium">+ Add</button>
                </div>
                {(form.identifier_start_patterns || []).length === 0 ? (
                  <p className="text-xs text-slate-400 italic">Matches from beginning</p>
                ) : (form.identifier_start_patterns || []).map((p, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <input
                      value={p.regex || ""}
                      onChange={e => updateScopePattern("start", i, e.target.value)}
                      placeholder="Start regex..."
                      className="flex-1 h-7 px-2 text-xs font-mono border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080]"
                    />
                    <button type="button" onClick={() => removeScopePattern("start", i)}
                      className="p-1 hover:bg-slate-100 rounded text-slate-400">
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
              </div>
              {/* End boundary */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-semibold text-slate-600">End Boundary</span>
                    <div className="flex gap-1">
                      {["and","or"].map(m => (
                        <button key={m} type="button"
                          onClick={() => setForm(f => ({...f, identifier_end_mode: m}))}
                          className={`h-5 px-1.5 text-[10px] font-bold rounded border transition-colors ${
                            form.identifier_end_mode === m ? "bg-[#006080] text-white border-[#006080]" : "bg-slate-50 text-slate-500 border-slate-200"
                          }`}>{m.toUpperCase()}</button>
                      ))}
                    </div>
                  </div>
                  <button type="button" onClick={() => addScopePattern("end")}
                    className="text-[11px] text-[#006080] hover:underline font-medium">+ Add</button>
                </div>
                {(form.identifier_end_patterns || []).length === 0 ? (
                  <p className="text-xs text-slate-400 italic">Matches to end of document</p>
                ) : (form.identifier_end_patterns || []).map((p, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <input
                      value={p.regex || ""}
                      onChange={e => updateScopePattern("end", i, e.target.value)}
                      placeholder="End regex..."
                      className="flex-1 h-7 px-2 text-xs font-mono border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080]"
                    />
                    <button type="button" onClick={() => removeScopePattern("end", i)}
                      className="p-1 hover:bg-slate-100 rounded text-slate-400">
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Fields ── */}
        <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Fields</h3>
            <span className="text-xs text-slate-400">{form.fields.filter(f => f.name && f.label).length} configured</span>
          </div>

          <div className="space-y-3">
            {form.fields.map((field, idx) => (
              <div
                key={field.id}
                data-testid={`field-row-${idx}`}
                className={`border border-slate-200 rounded-lg p-4 space-y-3 transition-opacity ${field.enabled === false ? "opacity-50 bg-slate-50" : "bg-slate-50/30"}`}
              >
                {/* Row 1: Label, Name, Type, Required, Enabled, Delete */}
                <div className="flex items-start gap-3">
                  <div className="hidden sm:flex items-center pt-2 text-slate-300">
                    <GripVertical size={14} />
                  </div>
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-4 gap-3">
                    <div className="sm:col-span-2">
                      <label className="text-xs text-slate-500 block mb-1">Label <span className="text-red-400">*</span></label>
                      <input
                        data-testid={`field-label-${idx}`}
                        value={field.label}
                        onChange={(e) => {
                          const lbl = e.target.value;
                          updateField(idx, "label", lbl);
                          if (!field._nameManual) {
                            updateField(idx, "name", lbl.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, ""));
                          }
                        }}
                        placeholder="Invoice Number"
                        className="w-full h-8 px-2.5 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">Field Name</label>
                      <input
                        data-testid={`field-name-${idx}`}
                        value={field.name}
                        onChange={(e) => { updateField(idx, "_nameManual", true); updateField(idx, "name", e.target.value); }}
                        placeholder="invoice_number"
                        className="w-full h-8 px-2.5 text-xs font-mono border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">Type</label>
                      <select
                        data-testid={`field-type-${idx}`}
                        value={field.field_type}
                        onChange={(e) => updateField(idx, "field_type", e.target.value)}
                        className="w-full h-8 px-2 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
                      >
                        {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5 pt-6 flex-shrink-0">
                    <label className="flex items-center gap-1 cursor-pointer" title="Required field">
                      <input
                        type="checkbox"
                        data-testid={`field-required-${idx}`}
                        checked={field.required}
                        onChange={(e) => updateField(idx, "required", e.target.checked)}
                        className="w-3.5 h-3.5 accent-[#006080]"
                      />
                      <span className="text-xs text-slate-500">Req</span>
                    </label>
                    {/* Enable/Disable toggle */}
                    <label className="flex items-center gap-1 cursor-pointer" title={field.enabled === false ? "Click to enable field" : "Click to disable field"}>
                      <div
                        data-testid={`field-enabled-toggle-${idx}`}
                        onClick={() => updateField(idx, "enabled", field.enabled === false ? true : false)}
                        className={`relative w-8 h-4 rounded-full transition-colors cursor-pointer flex-shrink-0 ${field.enabled !== false ? "bg-[#006080]" : "bg-slate-200"}`}
                      >
                        <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${field.enabled !== false ? "translate-x-4" : "translate-x-0.5"}`} />
                      </div>
                    </label>
                    <button
                      type="button"
                      data-testid={`field-delete-${idx}`}
                      onClick={() => removeField(idx)}
                      disabled={form.fields.length === 1}
                      className="p-1.5 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded disabled:opacity-30 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Row 2: Regex + Description */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Regex Pattern</label>
                    <input
                      data-testid={`field-regex-${idx}`}
                      value={field.regex || ""}
                      onChange={(e) => updateField(idx, "regex", e.target.value)}
                      placeholder="e.g., INV-\d{4}-\d+ (optional)"
                      className="w-full h-8 px-2.5 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080] font-mono bg-white"
                    />
                    <label className="flex items-center gap-1.5 cursor-pointer mt-1.5">
                      <input
                        type="checkbox"
                        data-testid={`field-def-regex-${idx}`}
                        checked={field.is_definition_regex || false}
                        onChange={(e) => updateField(idx, "is_definition_regex", e.target.checked)}
                        className="w-3 h-3 accent-[#006080]"
                      />
                      <span className="text-[11px] text-slate-400">Schema identifier regex</span>
                      {field.is_definition_regex && (
                        <span className="text-[9px] font-bold bg-[#E5F0F2] text-[#006080] px-1 py-0.5 rounded">ID</span>
                      )}
                    </label>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Description / Example</label>
                    <input
                      data-testid={`field-description-${idx}`}
                      value={field.description || ""}
                      onChange={(e) => updateField(idx, "description", e.target.value)}
                      placeholder="Hint for AI extraction"
                      className="w-full h-8 px-2.5 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
                    />
                  </div>
                </div>

                {/* Advanced toggle */}
                <button
                  type="button"
                  onClick={() => toggleExpand(idx)}
                  className="flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-[#006080] transition-colors w-full pt-1 border-t border-slate-100"
                >
                  <ChevronDown
                    size={12}
                    className={`transition-transform duration-200 ${expandedFields[idx] ? "rotate-0" : "-rotate-90"}`}
                  />
                  <span>Advanced options</span>
                  {hasAdvancedConfig(field) && (
                    <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-[#006080] text-white text-[9px] font-bold">
                      {(field.post_processing?.length || 0) + (field.regex_group > 0 ? 1 : 0) + (field.regex_multiple ? 1 : 0)}
                    </span>
                  )}
                </button>

                {/* Advanced section */}
                {expandedFields[idx] && (
                  <div className="pt-2 space-y-3 border-t border-slate-100">
                    {/* Group # + All Matches */}
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex items-center gap-1.5">
                        <label className="text-xs text-slate-500 flex-shrink-0">Group #:</label>
                        <input
                          type="number" min={0}
                          data-testid={`field-regex-group-${idx}`}
                          value={field.regex_group || 0}
                          onChange={e => updateField(idx, "regex_group", Math.max(0, parseInt(e.target.value) || 0))}
                          className="w-14 h-7 px-2 text-xs font-mono border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#006080] text-center bg-white"
                        />
                        <span className="text-xs text-slate-400">0 = full match</span>
                      </div>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid={`field-regex-multiple-${idx}`}
                          checked={field.regex_multiple || false}
                          onChange={e => updateField(idx, "regex_multiple", e.target.checked)}
                          className="w-3.5 h-3.5 accent-[#006080]"
                        />
                        <span className="text-xs text-slate-500">All matches (array)</span>
                      </label>
                    </div>

                    {/* Post-processing */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Post-Processing</span>
                        <AddStepDropdown onAdd={(type) => addFieldStep(idx, type)} />
                      </div>
                      {(field.post_processing?.length || 0) === 0 ? (
                        <p className="text-xs text-slate-400">No steps configured</p>
                      ) : (
                        <div className="space-y-1.5">
                          {field.post_processing.map((step, stepIdx) => (
                            <PostProcessStepRow
                              key={stepIdx}
                              step={step}
                              onRemove={() => removeFieldStep(idx, stepIdx)}
                              onUpdate={(key, val) => updateFieldStepParams(idx, stepIdx, key, val)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <button
            type="button"
            data-testid="add-field-button"
            onClick={addField}
            className="w-full h-9 border-2 border-dashed border-slate-200 text-xs font-medium text-slate-500 rounded-lg hover:border-[#006080] hover:text-[#006080] transition-colors flex items-center justify-center gap-1.5"
          >
            <Plus size={13} /> Add Field
          </button>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pb-8">
          <button
            type="button"
            onClick={() => navigate("/schemas")}
            className="flex-1 h-10 border border-slate-200 text-sm font-medium text-slate-600 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            data-testid="save-schema-button"
            disabled={saving}
            className="flex-1 h-10 bg-[#006080] text-white text-sm font-medium rounded-lg hover:bg-[#004B66] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Save size={14} />
            {saving ? "Saving..." : id ? "Update Schema" : "Create Schema"}
          </button>
        </div>
      </form>
    </div>
  );
}
